import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Alert,
  Container,
  FormFeedback,
  FormText,
  Input,
  Label,
  Form,
} from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { API } from "../../api/urls";
import defaultLogo from "../../assets/images/tahsilat360_logo2.png";
import tahsilat360Logo from "../../assets/images/tahsilat360_logo2_trim.png";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { COMPANY_TYPES } from "../../constants/companyTypes";
import { DISTRICTS } from "../../constants/districts";
import PhoneInput from "../../components/Forms/PhoneInput";
import { Turnstile } from "@marsidev/react-turnstile";
import { validateReferralCode } from "../../api/requests/referral-requests";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const turkishCities = Object.keys(DISTRICTS);

// Davet linkiyle gelen kod, ziyaretçi sekmeyi kapatıp günler sonra dönse de hatırlansın.
const REFERRAL_STORAGE_KEY = "tc_ref";
const REFERRAL_REMEMBER_DAYS = 30;

const readStoredReferral = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(REFERRAL_STORAGE_KEY) || "null");
    if (!saved?.code) return null;
    if (Date.now() - saved.savedAt > REFERRAL_REMEMBER_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return saved.code;
  } catch {
    return null;
  }
};

const storeReferral = (code) => {
  try { localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({ code, savedAt: Date.now() })); } catch {}
};

const clearStoredReferral = () => {
  try { localStorage.removeItem(REFERRAL_STORAGE_KEY); } catch {}
};

const WORKING_AREAS = [
  { value: "INFORMATION_TECHNOLOGY", label: "Bilişim ve Yazılım" },
  { value: "MANUFACTURING",          label: "Üretim ve İmalat" },
  { value: "RETAIL",                 label: "Perakende Satış" },
  { value: "HEALTHCARE",             label: "Sağlık ve Tıp" },
  { value: "FINANCE",                label: "Finans ve Sigorta" },
  { value: "EDUCATION",              label: "Eğitim Hizmetleri" },
  { value: "AUTOMOTIVE",             label: "Otomotiv Sektörü" },
  { value: "FOOD_AND_BEVERAGE",      label: "Gıda ve İçecek" },
  { value: "CONSTRUCTION",           label: "İnşaat Sektörü" },
  { value: "LOGISTICS",              label: "Lojistik ve Nakliye" },
  { value: "ENERGY",                 label: "Enerji" },
  { value: "OTHER",                  label: "Diğer" },
];

const customStyles = `
.btn-custom-blue {
  background-color: #221c5d;
  border-color: #221c5d;
  color: #fff;
}

.btn-custom-blue:hover {
  background-color: #1a1547;
  border-color: #1a1547;
  color: #fff;
}

.btn-custom-blue:focus,
.btn-custom-blue:active {
  background-color: #1a1547;
  border-color: #1a1547;
  color: #fff;
  box-shadow: 0 0 0 0.2rem rgba(34, 28, 93, 0.25);
}

.logo-container {
  width: 100%;
  max-width: clamp(260px, 35vw, 420px);
  margin: 0 auto 1.5rem;
}

.logo-container img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.login-logo {
  height: clamp(140px, 22vw, 260px);
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.auth-content-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
  padding: 2rem 0;
}

.auth-content {
  margin-top: auto;
  margin-bottom: auto;
}

@media (max-width: 576px) {
  .logo-container { max-width: 180px; margin-bottom: 1rem; }
  .login-logo { height: clamp(100px, 25vw, 140px); }
}

@media (min-width: 577px) and (max-width: 768px) {
  .logo-container { max-width: 200px; margin-bottom: 1.25rem; }
  .login-logo { height: clamp(110px, 18vw, 160px); }
}

@media (min-width: 1200px) {
  .auth-content-wrapper { padding: 3rem 0; }
}

.auth-footer {
  padding-block: clamp(0.5rem, 1.5vw, 1.25rem);
  font-size: clamp(0.65rem, 0.9vw, 0.875rem);
}

.footer-links {
  gap: clamp(0.3rem, 1vw, 0.75rem);
  font-size: clamp(0.65rem, 0.9vw, 0.875rem);
}

.footer-links span {
  cursor: pointer;
  transition: color 0.2s;
}

.footer-links span:hover { color: #495057 !important; }

@media (max-width: 576px) {
  .footer-links { font-size: 0.7rem; gap: 0.5rem !important; }
  .auth-footer img[alt="Tahsilat360"] { height: 70px !important; }
  .auth-footer .small { font-size: 0.65rem !important; }
}

@media (min-width: 577px) and (max-width: 768px) {
  .footer-links { font-size: 0.8rem; }
  .auth-footer img[alt="Tahsilat360"] { height: 80px !important; }
}

@media (min-width: 1200px) {
  .footer-links { font-size: 0.875rem; gap: 1rem !important; }
  .auth-footer img[alt="Tahsilat360"] { height: 100px !important; }
}

.auth-footer .border-top { border-color: #dee2e6 !important; }

.auth-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.auth-scroll {
  flex: 1;
  overflow-y: auto;
}

.custom-primary-link {
  color: #221c5d !important;
  text-decoration: none;
}

.custom-primary-link:hover {
  color: #1a1547 !important;
  text-decoration: underline;
}

.register-success-icon {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #221c5d, #2d2570);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}
`;

const CONTRACT_ENDPOINT = {
  CANCELLATION: API.contract_cancel_return_privacy,
  KVKK: API.contract_personal_data,
  SERVICE: API.contract_service_agreement,
};

const SelfRegisterPage = () => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [error, setError] = useState("");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const turnstileRef = React.useRef(null);
  const location = useLocation();
  const [referral, setReferral] = useState(null);

  const [contractModal, setContractModal] = useState({
    open: false,
    title: "",
    html: "",
    busy: false,
  });

  const [contactModal, setContactModal] = useState({
    open: false,
    busy: false,
    data: null,
    error: null,
  });

  useEffect(() => {
    document.title = "Hesap Oluştur | Tahsilat360";
  }, []);

  useEffect(() => {
    let fromUrl = null;
    try { fromUrl = new URLSearchParams(location.search).get("ref"); } catch {}
    const code = (fromUrl || readStoredReferral() || "").trim().toUpperCase();
    if (!code) return;
    if (fromUrl) storeReferral(code);
    setReferral({ code, status: "checking" });
    validateReferralCode(code)
      .then((res) => {
        const data = res?.data;
        setReferral(data?.valid
          ? { code, status: "valid", months: data.referredRewardMonths }
          : { code, status: "invalid", message: data?.message || "Davet kodu geçersiz" });
      })
      .catch(() => setReferral({ code, status: "unknown" }));
  }, [location.search]);

  const removeReferral = () => {
    clearStoredReferral();
    setReferral(null);
  };

  // Linksiz gelen kişi kodu elle yazabilir; kod bir firmaya ait değilse kabul edilmez.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [manualError, setManualError] = useState("");
  const [manualChecking, setManualChecking] = useState(false);

  const applyManualCode = async () => {
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    setManualError("");
    setManualChecking(true);
    try {
      const res = await validateReferralCode(code);
      const data = res?.data;
      if (data?.valid) {
        setReferral({ code, status: "valid", months: data.referredRewardMonths });
        setManualCode("");
        setManualOpen(false);
      } else {
        setManualError(data?.message || "Davet kodu geçersiz");
      }
    } catch {
      setManualError("Kod şu an kontrol edilemedi, lütfen tekrar deneyin.");
    } finally {
      setManualChecking(false);
    }
  };

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(API.get_company_logo, { responseType: "blob" });
        if (res.data && res.data.size > 0) {
          setCompanyLogo(URL.createObjectURL(res.data));
        } else {
          setCompanyLogo(defaultLogo);
        }
      } catch {
        setCompanyLogo(defaultLogo);
      }
    };
    fetchLogo();
  }, []);

  const openContractModal = async (type) => {
    setContractModal((prev) => ({ ...prev, open: true, busy: true, html: "", title: "" }));
    try {
      const urlFunc = CONTRACT_ENDPOINT[type];
      const url = urlFunc(null);
      const res = await axios.get(url);
      if (res.data.statusCode !== 200 || !res.data.data)
        throw new Error(res.data.message || "Sözleşme bulunamadı");

      const titles = {
        CANCELLATION: "İptal / İade Koşulları ve Gizlilik",
        KVKK: "Kişisel Veriler Politikası",
        SERVICE: "Hizmet Sözleşmesi",
      };
      setContractModal({ open: true, busy: false, html: res.data.data, title: titles[type] });
    } catch (err) {
      setContractModal({
        open: true,
        busy: false,
        html: `<p class="text-danger">Sözleşme bulunamadı.</p>`,
        title: "Uyarı",
      });
      console.error("Contract fetch error:", err);
    }
  };

  const openContactModal = async () => {
    setContactModal({ open: true, busy: true, data: null, error: null });
    try {
      const res = await axios.get(API.get_company_info_by_domain);
      if (res.data.statusCode !== 200 || !res.data.data)
        throw new Error(res.data.message || "Bilgi bulunamadı");
      setContactModal({ open: true, busy: false, data: res.data.data, error: null });
    } catch (err) {
      console.error("Contact fetch error:", err);
      setContactModal({ open: true, busy: false, data: null, error: "İletişim bilgileri alınamadı" });
    }
  };

  const formik = useFormik({
    initialValues: {
      companyName: "",
      contactFullName: "",
      email: "",
      phoneNumber: "",
      companyType: "",
      workingArea: "",
      subdomain: "",
      domain: "",
      city: "",
      district: "",
      address: "",
      invoiceTaxNumber: "",
      invoiceTcNumber: "",
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required("Firma adı zorunlu"),
      contactFullName: Yup.string().required("Ad Soyad zorunlu"),
      email: Yup.string().email("Geçerli bir e-posta giriniz").required("E-posta zorunlu"),
      phoneNumber: Yup.string()
        .matches(/^\+90\d{10}$/, "Geçerli bir 10 haneli cep numarası giriniz")
        .required("Telefon numarası zorunlu"),
      companyType: Yup.string().required("Firma türü zorunlu"),
      workingArea: Yup.string().required("Faaliyet alanı zorunlu"),
      city: Yup.string().required("İl seçimi zorunlu"),
      address: Yup.string().max(512, "Adres en fazla 512 karakter olabilir"),
      subdomain: Yup.string().test(
        "subdomain-format",
        "Subdomain küçük harf, rakam ve tire (-) içermeli (3-100)",
        (val) => !val || /^[a-z0-9-]{3,100}$/.test(val)
      ),
      domain: Yup.string().test(
        "domain-format",
        "Domain küçük harf, rakam, nokta ve tire (-) içermeli (3-100)",
        (val) => !val || /^[a-z0-9.-]{3,100}$/.test(val)
      ),
      // Şahıs ise TC zorunlu (11 hane), diğerleri opsiyonel (11 hane format kontrolü)
      invoiceTcNumber: Yup.string().when("companyType", {
        is: "SOLE_PROPRIETORSHIP",
        then: (schema) =>
          schema
            .required("Şahıs firması için TC Kimlik numarası zorunludur")
            .matches(/^\d{11}$/, "TC Kimlik numarası 11 haneli olmalıdır"),
        otherwise: (schema) =>
          schema.test("tc-format", "TC kimlik numarası 11 haneli olmalıdır", (val) =>
            !val || /^\d{11}$/.test(val)
          ),
      }),
      // Şahıs dışı ise VKN zorunlu (10 hane), şahıs ise opsiyonel (10 hane format kontrolü)
      invoiceTaxNumber: Yup.string().when("companyType", {
        is: (val) => val && val !== "SOLE_PROPRIETORSHIP",
        then: (schema) =>
          schema
            .required("Vergi kimlik numarası zorunludur")
            .matches(/^\d{10}$/, "Vergi numarası 10 haneli olmalıdır"),
        otherwise: (schema) =>
          schema.test("vkn-format", "Vergi numarası 10 haneli olmalıdır", (val) =>
            !val || /^\d{10}$/.test(val)
          ),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setError("");
      // Captcha — token yoksa backend'e gitme
      if (!captchaToken) {
        setCaptchaError("Lütfen güvenlik doğrulamasını tamamlayın.");
        setSubmitting(false);
        return;
      }
      setCaptchaError("");
      try {
        const { city, district, ...restValues } = values;

        // İl/ilçe/adres birleştir (CompanyCreatePage ile aynı format)
        const baseAddress = restValues.address?.trim() || "";
        const ilIlcePart =
          city && district
            ? `${district} / ${city}`
            : city
              ? city
              : district || "";
        const composedAddress = baseAddress
          ? `${baseAddress} - ${ilIlcePart}`
          : ilIlcePart;

        // Boş string alanları payload'dan çıkar
        const payload = Object.fromEntries(
          Object.entries({ ...restValues, address: composedAddress }).filter(([, v]) => v !== "")
        );
        payload.captchaToken = captchaToken;
        if (referral?.code && referral.status !== "invalid") {
          payload.referralCode = referral.code;
        }
        const res = await axios.post(API.register, payload);
        clearStoredReferral();
        setSuccessData(res.data?.data || {});
      } catch (err) {
        const msg = err.response?.data?.message || "Bir hata oluştu. Lütfen tekrar deneyin.";
        setError(msg);
        // Token tek kullanımlık — backend reddetti veya başka hata olduysa yeni token al
        try { turnstileRef.current?.reset(); } catch {}
        setCaptchaToken("");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Firma türü değişince kimlik/vergi alanlarını sıfırla
  const handleCompanyTypeChange = (e) => {
    formik.setFieldValue("companyType", e.target.value);
    formik.setFieldValue("invoiceTaxNumber", "");
    formik.setFieldValue("invoiceTcNumber", "");
    formik.setFieldTouched("invoiceTaxNumber", false);
    formik.setFieldTouched("invoiceTcNumber", false);
  };

  const isSoleProp = formik.values.companyType === "SOLE_PROPRIETORSHIP";

  const logoSrc = companyLogo || defaultLogo;

  return (
    <React.Fragment>
      <style>{customStyles}</style>
      <div
        className="auth-page"
        style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <div
          className="auth-scroll flex-grow-1"
          style={{ overflowY: "auto", overflowX: "hidden" }}
        >
          <Container fluid className="p-0 h-100 d-flex align-items-center">
            <Row className="g-0 w-100 justify-content-center">
              <Col lg={6} md={8} xl={5} xxl={4} style={{ maxHeight: "100%", overflowY: "auto" }}>
                <div
                  className="auth-full-page-content d-flex"
                  style={{ minHeight: "100%" }}
                >
                  <div className="w-100" style={{ overflow: "hidden" }}>
                    <div className="d-flex flex-column h-100 auth-content-wrapper">
                      <div className="auth-content my-auto">
                        {/* Logo */}
                        <div className="logo-container text-center logo-absolute-container">
                          <Link to="/login" className="d-block auth-logo text-center">
                            <img
                              src={logoSrc}
                              alt="Logo"
                              className="login-logo img-fluid"
                              onError={() => setCompanyLogo(defaultLogo)}
                            />
                          </Link>
                        </div>

                        {successData ? (
                          /* ── Başarı ekranı ── */
                          <div className="text-center">
                            <div className="register-success-icon">
                              <i
                                className="mdi mdi-check-bold"
                                style={{ fontSize: "36px", color: "#fff" }}
                              />
                            </div>
                            <h5 className="mb-2">Başvurunuz Alındı!</h5>
                            <p className="text-muted mb-4">
                              Hesap başvurunuz başarıyla oluşturuldu. Sistem yöneticimiz
                              en kısa sürede sizinle iletişime geçecektir.
                            </p>

                            {successData.companyCode && (
                              <div
                                className="mb-3 p-3 rounded"
                                style={{
                                  background: "#f0f4ff",
                                  border: "1px solid #d0d9f5",
                                  textAlign: "left",
                                }}
                              >
                                <p className="mb-1 small text-muted">Firma Kodunuz</p>
                                <strong style={{ fontSize: "1.1rem", color: "#221c5d" }}>
                                  {successData.companyCode}
                                </strong>
                                <p className="mt-2 mb-0 small text-muted">
                                  Giriş yaparken bu kodu kullanacaksınız. Lütfen not alın.
                                </p>
                              </div>
                            )}

                            <Link to="/login" className="btn btn-custom-blue w-100 mt-2">
                              Giriş Sayfasına Git
                            </Link>
                          </div>
                        ) : (
                          /* ── Kayıt formu ── */
                          <>
                            <div className="text-center">
                              <h5 className="mb-0">Hesap Oluştur</h5>
                              <p className="text-muted mt-2">
                                Aşağıdaki bilgileri doldurarak hesap başvurusu yapın.
                              </p>
                            </div>

                            {error && (
                              <Alert color="danger" style={{ marginTop: "13px" }}>
                                {error}
                              </Alert>
                            )}

                            <Form
                              className="mt-3 pt-2"
                              onSubmit={(e) => {
                                e.preventDefault();
                                formik.handleSubmit();
                                return false;
                              }}
                            >
                              {/* Satır 1: Firma Adı + Ad Soyad */}
                              <Row className="g-2 mb-3">
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Firma Adı <span className="text-danger">*</span></Label>
                                  <Input
                                    name="companyName"
                                    type="text"
                                    placeholder="Firma adını girin"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.companyName}
                                    invalid={!!(formik.touched.companyName && formik.errors.companyName)}
                                  />
                                  {formik.touched.companyName && formik.errors.companyName && (
                                    <FormFeedback>{formik.errors.companyName}</FormFeedback>
                                  )}
                                </Col>
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Ad Soyad <span className="text-danger">*</span></Label>
                                  <Input
                                    name="contactFullName"
                                    type="text"
                                    placeholder="Yetkili kişinin adı ve soyadı"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.contactFullName}
                                    invalid={!!(formik.touched.contactFullName && formik.errors.contactFullName)}
                                  />
                                  {formik.touched.contactFullName && formik.errors.contactFullName && (
                                    <FormFeedback>{formik.errors.contactFullName}</FormFeedback>
                                  )}
                                </Col>
                              </Row>

                              {/* Satır 2: E-posta + Telefon */}
                              <Row className="g-2 mb-3">
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">E-posta <span className="text-danger">*</span></Label>
                                  <Input
                                    name="email"
                                    type="email"
                                    placeholder="E-posta adresinizi girin"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.email}
                                    invalid={!!(formik.touched.email && formik.errors.email)}
                                  />
                                  {formik.touched.email && formik.errors.email && (
                                    <FormFeedback>{formik.errors.email}</FormFeedback>
                                  )}
                                </Col>
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Telefon Numarası <span className="text-danger">*</span></Label>
                                  <PhoneInput
                                    label=""
                                    name="phoneNumber"
                                    value={formik.values.phoneNumber}
                                    onChange={formik.handleChange}
                                  />
                                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                                    <div className="text-danger" style={{ fontSize: "0.875em", marginTop: "0.25rem" }}>
                                      {formik.errors.phoneNumber}
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              {/* Satır 3: Firma Türü + Faaliyet Alanı */}
                              <Row className="g-2 mb-3">
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Firma Türü <span className="text-danger">*</span></Label>
                                  <Input
                                    name="companyType"
                                    type="select"
                                    onChange={handleCompanyTypeChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.companyType}
                                    invalid={!!(formik.touched.companyType && formik.errors.companyType)}
                                  >
                                    <option value="">Firma türü seçiniz</option>
                                    {COMPANY_TYPES.map((ct) => (
                                      <option key={ct.value} value={ct.value}>
                                        {ct.label}
                                      </option>
                                    ))}
                                  </Input>
                                  {formik.touched.companyType && formik.errors.companyType ? (
                                    <FormFeedback>{formik.errors.companyType}</FormFeedback>
                                  ) : formik.values.companyType ? (
                                    <small className="text-muted">
                                      {isSoleProp
                                        ? "Şahıs firması için TC Kimlik zorunludur."
                                        : "Diğer firma tipleri için Vergi Kimlik No zorunludur."}
                                    </small>
                                  ) : null}
                                </Col>
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Faaliyet Alanı <span className="text-danger">*</span></Label>
                                  <Input
                                    name="workingArea"
                                    type="select"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.workingArea}
                                    invalid={!!(formik.touched.workingArea && formik.errors.workingArea)}
                                  >
                                    <option value="">Alan seçiniz</option>
                                    {WORKING_AREAS.map((wa) => (
                                      <option key={wa.value} value={wa.value}>
                                        {wa.label}
                                      </option>
                                    ))}
                                  </Input>
                                  {formik.touched.workingArea && formik.errors.workingArea && (
                                    <FormFeedback>{formik.errors.workingArea}</FormFeedback>
                                  )}
                                </Col>
                              </Row>

                              {/* Satır 4: Alt Domain + Domain */}
                              <Row className="g-2 mb-3">
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Alt Domain</Label>
                                  <Input
                                    name="subdomain"
                                    type="text"
                                    placeholder="orneksirket"
                                    maxLength={100}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.subdomain}
                                    invalid={!!(formik.touched.subdomain && formik.errors.subdomain)}
                                  />
                                  {formik.touched.subdomain && formik.errors.subdomain ? (
                                    <FormFeedback>{formik.errors.subdomain}</FormFeedback>
                                  ) : (
                                    <FormText>
                                      Küçük harf, rakam ve tire (-); 3–100 karakter.{" "}
                                      Örn: <strong>sirketiniz.tahsilat360.com</strong>
                                    </FormText>
                                  )}
                                </Col>
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">Domain</Label>
                                  <Input
                                    name="domain"
                                    type="text"
                                    placeholder="ornek.com"
                                    maxLength={100}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.domain}
                                    invalid={!!(formik.touched.domain && formik.errors.domain)}
                                  />
                                  {formik.touched.domain && formik.errors.domain ? (
                                    <FormFeedback>{formik.errors.domain}</FormFeedback>
                                  ) : (
                                    <FormText>harf, rakam, nokta, tire (3-100)</FormText>
                                  )}
                                </Col>
                              </Row>

                              {/* Satır 5: İl + İlçe */}
                              <Row className="g-2 mb-3">
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">İl <span className="text-danger">*</span></Label>
                                  <Input
                                    name="city"
                                    type="select"
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      formik.setFieldValue("city", val);
                                      formik.setFieldValue("district", "");
                                      setDistrictOptions(DISTRICTS[val] || []);
                                    }}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.city}
                                    invalid={!!(formik.touched.city && formik.errors.city)}
                                  >
                                    <option value="">İl seçiniz</option>
                                    {turkishCities.map((c) => (
                                      <option key={c} value={c}>{c}</option>
                                    ))}
                                  </Input>
                                  {formik.touched.city && formik.errors.city && (
                                    <FormFeedback>{formik.errors.city}</FormFeedback>
                                  )}
                                </Col>
                                <Col xs={12} sm={6}>
                                  <Label className="form-label">İlçe</Label>
                                  <Input
                                    name="district"
                                    type="select"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.district}
                                    disabled={!formik.values.city}
                                  >
                                    <option value="">İlçe seçiniz</option>
                                    {districtOptions.map((d) => (
                                      <option key={d} value={d}>{d}</option>
                                    ))}
                                  </Input>
                                </Col>
                              </Row>

                              {/* Adres detay */}
                              <div className="mb-3">
                                <Label className="form-label">Adres</Label>
                                <Input
                                  name="address"
                                  type="textarea"
                                  rows={2}
                                  placeholder="Mahalle, cadde, sokak, no vb."
                                  maxLength={512}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.address}
                                  invalid={!!(formik.touched.address && formik.errors.address)}
                                />
                                {formik.touched.address && formik.errors.address && (
                                  <FormFeedback>{formik.errors.address}</FormFeedback>
                                )}
                              </div>

                              {/* TC Kimlik No — sadece Şahıs İşletmesi için zorunlu */}
                              {isSoleProp ? (
                                <div className="mb-3">
                                  <Label className="form-label">
                                    TC Kimlik No <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    name="invoiceTcNumber"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="11 haneli TC kimlik numarası"
                                    maxLength={11}
                                    onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                    onChange={(e) =>
                                      formik.setFieldValue("invoiceTcNumber", e.target.value.replace(/\D/g, ""))
                                    }
                                    onBlur={formik.handleBlur}
                                    value={formik.values.invoiceTcNumber}
                                    invalid={!!(formik.touched.invoiceTcNumber && formik.errors.invoiceTcNumber)}
                                  />
                                  {formik.touched.invoiceTcNumber && formik.errors.invoiceTcNumber && (
                                    <FormFeedback>{formik.errors.invoiceTcNumber}</FormFeedback>
                                  )}
                                </div>
                              ) : formik.values.companyType ? (
                                /* Vergi Kimlik No — şahıs dışı tüm firma türleri için zorunlu */
                                <div className="mb-3">
                                  <Label className="form-label">
                                    Vergi Kimlik No <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    name="invoiceTaxNumber"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="10 haneli vergi kimlik numarası"
                                    maxLength={10}
                                    onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                    onChange={(e) =>
                                      formik.setFieldValue("invoiceTaxNumber", e.target.value.replace(/\D/g, ""))
                                    }
                                    onBlur={formik.handleBlur}
                                    value={formik.values.invoiceTaxNumber}
                                    invalid={!!(formik.touched.invoiceTaxNumber && formik.errors.invoiceTaxNumber)}
                                  />
                                  {formik.touched.invoiceTaxNumber && formik.errors.invoiceTaxNumber && (
                                    <FormFeedback>{formik.errors.invoiceTaxNumber}</FormFeedback>
                                  )}
                                </div>
                              ) : null}

                              {referral && (
                                <Alert
                                  color={referral.status === "invalid" ? "warning" : "info"}
                                  className="py-2 d-flex justify-content-between align-items-start gap-2"
                                >
                                  <div>
                                    <div>
                                      <i className="bx bx-gift me-1" />
                                      Davet kodu: <strong>{referral.code}</strong>
                                    </div>
                                    <small>
                                      {referral.status === "checking" && "Kontrol ediliyor..."}
                                      {referral.status === "valid" && referral.months > 0 &&
                                        `Paket seçtiğinizde aboneliğinize ${referral.months} ay hediye eklenecek.`}
                                      {referral.status === "valid" && !(referral.months > 0) && "Davet kodunuz geçerli."}
                                      {referral.status === "invalid" && `${referral.message}. Başvurunuz davet kodu olmadan gönderilir.`}
                                      {referral.status === "unknown" && "Kod başvuru sırasında kontrol edilecek."}
                                    </small>
                                  </div>
                                  <button type="button" className="btn btn-link btn-sm p-0" onClick={removeReferral}>
                                    Kaldır
                                  </button>
                                </Alert>
                              )}

                              {!referral && (
                                <div className="mb-3">
                                  {!manualOpen ? (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0"
                                      onClick={() => setManualOpen(true)}
                                    >
                                      <i className="bx bx-gift me-1" />
                                      Davet kodunuz var mı?
                                    </button>
                                  ) : (
                                    <>
                                      <Label className="form-label">Davet Kodu</Label>
                                      <div className="d-flex gap-2">
                                        <Input
                                          value={manualCode}
                                          maxLength={16}
                                          placeholder="Örn. K7QM4R9X"
                                          invalid={!!manualError}
                                          onChange={(e) => {
                                            setManualCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase());
                                            setManualError("");
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              applyManualCode();
                                            }
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-custom-blue"
                                          style={{ whiteSpace: "nowrap" }}
                                          disabled={!manualCode.trim() || manualChecking}
                                          onClick={applyManualCode}
                                        >
                                          {manualChecking ? "Kontrol..." : "Uygula"}
                                        </button>
                                      </div>
                                      {manualError && (
                                        <div className="invalid-feedback d-block mt-1">{manualError}</div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}

                              {/* Cloudflare Turnstile captcha */}
                              <Row className="g-2 mb-3">
                                <Col xs={12}>
                                  <Label className="form-label">
                                    Güvenlik Doğrulaması <span className="text-danger">*</span>
                                  </Label>
                                  <div>
                                    {!TURNSTILE_SITE_KEY ? (
                                      <Alert color="warning" className="mb-0 py-2">
                                        <i className="bx bx-error-circle me-2" />
                                        Captcha yapılandırılmamış (REACT_APP_TURNSTILE_SITE_KEY tanımlı değil). Geliştirici ortamı için <code>.env</code> dosyasına ekleyip dev server'ı yeniden başlatın.
                                      </Alert>
                                    ) : (
                                      <Turnstile
                                        ref={turnstileRef}
                                        siteKey={TURNSTILE_SITE_KEY}
                                        options={{ theme: "light", language: "tr" }}
                                        onSuccess={(token) => {
                                          setCaptchaToken(token);
                                          setCaptchaError("");
                                        }}
                                        onExpire={() => setCaptchaToken("")}
                                        onError={() => {
                                          setCaptchaToken("");
                                          setCaptchaError("Doğrulama yüklenemedi, sayfayı yenileyin.");
                                        }}
                                      />
                                    )}
                                  </div>
                                  {captchaError && (
                                    <div className="invalid-feedback d-block mt-1">{captchaError}</div>
                                  )}
                                </Col>
                              </Row>

                              <div className="mt-4 d-grid">
                                <button
                                  className="btn btn-custom-blue btn-block"
                                  type="submit"
                                  disabled={formik.isSubmitting}
                                >
                                  {formik.isSubmitting ? "Gönderiliyor..." : "Başvuruyu Gönder"}
                                </button>
                              </div>
                            </Form>

                            <div className="mt-4 text-center">
                              <p className="text-muted mb-0">
                                Zaten hesabınız var mı?{" "}
                                <Link to="/login" className="custom-primary-link fw-semibold">
                                  Giriş Yap
                                </Link>
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* Footer */}
        <footer
          className="auth-footer bg-white border-top"
          style={{ flexShrink: 0, paddingBottom: "10px" }}
        >
          <Container fluid>
            <Row className="align-items-center py-0">
              <Col lg={12} className="text-center mb-2">
                <div className="footer-links d-flex justify-content-center flex-wrap gap-2 mb-2">
                  <span
                    className="text-muted text-decoration-none small"
                    role="button"
                    onClick={openContactModal}
                  >
                    İletişim
                  </span>
                  <span
                    className="text-muted text-decoration-none small"
                    role="button"
                    onClick={() => openContractModal("CANCELLATION")}
                  >
                    İptal, İade Koşulları ve Gizlilik
                  </span>
                  <span
                    className="text-muted text-decoration-none small"
                    role="button"
                    onClick={() => openContractModal("KVKK")}
                  >
                    Kişisel Veriler Politikası
                  </span>
                  <span
                    className="text-muted text-decoration-none small"
                    role="button"
                    onClick={() => openContractModal("SERVICE")}
                  >
                    Hizmet Sözleşmesi
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="border-top p-0">
              <Col className="text-center p-0">
                <div className="text-center mt-1" style={{ marginBottom: "0px" }}>
                  <a
                    href="https://tahsilat360.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src={tahsilat360Logo}
                      alt="Tahsilat360"
                      style={{ maxHeight: "30px" }}
                    />
                  </a>
                </div>
                <p className="text-muted small mb-0">
                  <a
                    href="https://tahsilat360.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted text-decoration-none"
                  >
                    © {new Date().getFullYear()} Tahsilat360 | Online Tahsilat Sistemi. Tüm hakları saklıdır.
                  </a>
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>

      {/* Contract Modal */}
      <Modal
        isOpen={contractModal.open}
        toggle={() => setContractModal((p) => ({ ...p, open: false }))}
        size="lg"
      >
        <ModalHeader toggle={() => setContractModal((p) => ({ ...p, open: false }))}>
          {contractModal.title}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {contractModal.busy ? (
            <div className="text-center py-4">
              <span className="spinner-border" />
            </div>
          ) : (
            <div
              style={{ whiteSpace: "pre-wrap" }}
              dangerouslySetInnerHTML={{ __html: contractModal.html }}
            />
          )}
        </ModalBody>
      </Modal>

      {/* Contact Modal */}
      <Modal
        isOpen={contactModal.open}
        toggle={() => setContactModal((p) => ({ ...p, open: false }))}
      >
        <ModalHeader toggle={() => setContactModal((p) => ({ ...p, open: false }))}>
          İletişim Bilgileri
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {contactModal.busy && (
            <div className="text-center py-4">
              <span className="spinner-border" />
            </div>
          )}
          {!contactModal.busy && contactModal.error && (
            <p className="text-danger">{contactModal.error}</p>
          )}
          {!contactModal.busy && contactModal.data && (
            <div style={{ whiteSpace: "pre-wrap" }}>
              <strong>{contactModal.data.companyName}</strong>
              {"\n\n"}
              {contactModal.data.phoneNumber && (
                <>
                  <strong>Telefon:</strong> {contactModal.data.phoneNumber}
                  {"\n"}
                </>
              )}
              {contactModal.data.contactEmail && (
                <>
                  <strong>E-posta:</strong> {contactModal.data.contactEmail}
                  {"\n"}
                </>
              )}
              {contactModal.data.address && (
                <>
                  <strong>Adres:</strong>
                  {"\n"}
                  {contactModal.data.address}
                </>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default SelfRegisterPage;
